import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { EyeIconSmall, ThreeDotsIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/popover/index';
import { TableRow } from '../../../components/table/index';
import { useFormat } from '../../../hooks/index';
import { selectedCompanyStorage } from '../../../storage/company/index';
import { Safe, safeCapTypeTitle } from '../../../types/safes.types';
import { formatDisplayValue } from '../../../utils/format-display-value.util';
import { Column, columnOrderMap, columnTranslationKey } from './safe-ledger.types';

export type TableItemProps = { data: Safe; mode?: 'TABLE'; openViewModal?: () => void };

export const SafeItem: FC<TableItemProps> = ({
  data: { id, investment, floorValue, issueDate, maturityDate, discount, capValue, capType },
  mode,
  openViewModal,
}) => {
  const { format } = useFormat();
  const company = selectedCompanyStorage(({ company }) => company);
  const currency = company?.currency?.symbol || company?.currency?.iso3.toUpperCase();

  const payload: { id: string } & Record<Column, number | ReactNode | string> = {
    [Column.INVESTMENT]: formatDisplayValue(investment, { prefix: currency }),
    [Column.AMOUNT_DUE]: '-',
    [Column.ISSUE_DATE]: format(issueDate),
    [Column.CAP_VALUE]: formatDisplayValue(capValue),
    [Column.CAP_TYPE]:
      capValue && capType ? <AppFormattedMessage id={safeCapTypeTitle[capType]} /> : '-',
    [Column.DISCOUNT]: formatDisplayValue(discount, { multiply: 100 }),
    [Column.FLOOR]: formatDisplayValue(floorValue),
    [Column.MATURITY_DATE]: format(maturityDate) || '-',
    [Column.EMPTY]: (
      <Popover>
        <PopoverTrigger className="h-fit w-fit rounded data-[state=open]:shadow-md">
          <div className="flex size-6 rotate-90 items-center justify-center rounded bg-transparent transition-colors hover:bg-gray-100">
            <ThreeDotsIcon />
          </div>
        </PopoverTrigger>
        <PopoverContent className="flex w-fit flex-col gap-[2px] rounded p-2 font-normal shadow-2xl [&>*]:font-inter [&>*]:font-normal">
          <PopoverClose>
            <div
              className="flex w-full items-center gap-2 p-[6px] pr-8 text-sm text-gray-700 hover:bg-gray-50"
              onClick={openViewModal}
            >
              <EyeIconSmall />
              <span className="text-sm font-normal text-gray-700">
                <AppFormattedMessage id="VIEW_SAFE" />
              </span>
            </div>
          </PopoverClose>
        </PopoverContent>
      </Popover>
    ),
    id,
  };

  if (mode === 'TABLE')
    return (
      <TableRow
        columnOrder={columnOrderMap}
        fields={payload}
        labelClassName="text-xs font-[450] text-gray-700"
      />
    );

  return (
    <div className="flex w-full flex-col divide-y divide-gray-100 rounded-md bg-white shadow-sm">
      {columnOrderMap.map(
        (column) =>
          column !== Column.EMPTY &&
          !openViewModal && (
            <div
              className={twMerge(
                'flex items-center justify-between gap-2 text-nowrap px-4 py-[10px] text-sm font-[450] text-gray-700',
              )}
              key={column}
            >
              <AppFormattedMessage id={columnTranslationKey[column]} />
              <span>{payload[column]}</span>
            </div>
          ),
      )}
    </div>
  );
};
